import { useEffect } from "react";

const DEFAULT_OG_IMAGE =
  "https://customer-assets.emergentagent.com/job_leads-paid-ads/artifacts/c8bafmph_Rekisa%20white.png";

/**
 * usePageMeta — minimal SPA meta injector.
 *
 * Sets document.title, meta[name=description], canonical link, Open Graph,
 * Twitter Card meta and injects a per-page JSON-LD <script>. Cleans up
 * the JSON-LD on route change so each page has its own structured data.
 */
export function usePageMeta({ title, description, canonical, jsonLd, ogImage, ogImageAlt }) {
  useEffect(() => {
    if (title) document.title = title;

    const ensureMeta = (name, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    const ensureOg = (property, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[property="${property}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("property", property);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    ensureMeta("description", description);
    ensureOg("og:title", title);
    ensureOg("og:description", description);
    if (canonical) ensureOg("og:url", canonical);
    const img = ogImage || DEFAULT_OG_IMAGE;
    ensureOg("og:image", img);
    ensureOg("og:image:alt", ogImageAlt || "Rekisa logo");
    ensureMeta("twitter:card", "summary_large_image");
    ensureMeta("twitter:title", title);
    ensureMeta("twitter:description", description);
    ensureMeta("twitter:image", img);
    ensureMeta("twitter:image:alt", ogImageAlt || "Rekisa logo");

    let canonicalEl = null;
    if (canonical) {
      canonicalEl = document.querySelector('link[rel="canonical"]');
      if (!canonicalEl) {
        canonicalEl = document.createElement("link");
        canonicalEl.setAttribute("rel", "canonical");
        document.head.appendChild(canonicalEl);
      }
      canonicalEl.setAttribute("href", canonical);
    }

    let jsonLdEl = null;
    if (jsonLd) {
      jsonLdEl = document.createElement("script");
      jsonLdEl.type = "application/ld+json";
      jsonLdEl.setAttribute("data-page-jsonld", "true");
      jsonLdEl.text = JSON.stringify(jsonLd);
      document.head.appendChild(jsonLdEl);
    }

    return () => {
      if (jsonLdEl && jsonLdEl.parentNode) jsonLdEl.parentNode.removeChild(jsonLdEl);
    };
  }, [title, description, canonical, jsonLd, ogImage, ogImageAlt]);
}
